import api from "@/utils/api";
import { ApiResponse } from "@/utils/api/ApiProps";

// ===== TYPES =====

export interface DripEmailTemplate {
  id: number;
  uuid: string;
  client_id_fk: number | null;
  template_name: string;
  template_slug: string;
  description: string | null;
  category: 'welcome' | 'nurture' | 'educational' | 're_engagement' | 'promotional' | 'other';
  subject_line: string;
  preheader_text: string | null;
  html_body: string;
  plain_text_body: string | null;
  variables: Array<{
    name: string;
    label: string;
    default: string;
    required: boolean;
    leadField?: string;
  }>;
  thumbnail_url: string | null;
  is_active: boolean;
  is_system_template: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

// Per-step channel — the node types the journey engine supports.
// 'ai_call' is reserved (Engati) and disabled in the UI for now.
export type DripNodeType =
  | 'email'
  | 'whatsapp'
  | 'delay'
  | 'condition'
  | 'agent_call'
  | 'auto_dial'
  | 'ai_call';

// Where a step's delay is measured from. The last two are product-journey anchors.
export type DripDelayFrom =
  | 'enrollment'
  | 'previous_step'
  | 'order_delivered'
  | 'reorder_due_date';

export type DripConditionType =
  | 'email_opened'
  | 'email_clicked'
  | 'time_based'
  | 'reordered'
  | 'call_connected'
  | 'disposition_is'
  | 'whatsapp_replied';

export interface DripCampaignStep {
  id: number;
  uuid: string;
  campaign_id_fk: number;
  template_id_fk: number | null;
  node_type: DripNodeType;
  integration_template_id_fk: number | null;
  whatsapp_template_name: string | null;
  whatsapp_template_language: string | null;
  whatsapp_template_params: any;
  config: Record<string, any> | null;
  step_name: string;
  step_order: number;
  delay_value: number;
  delay_unit: 'minutes' | 'hours' | 'days' | 'weeks';
  delay_from: DripDelayFrom;
  conditions: any;
  condition_type?: DripConditionType | null;
  custom_subject_line: string | null;
  parameter_mapping: Record<string, string>;
  is_active: boolean;
  total_sent: number;
  total_failed: number;
  template?: DripEmailTemplate;
  created_at: string;
  updated_at: string;
}

export interface DripCampaign {
  id: number;
  uuid: string;
  client_id_fk: number;
  campaign_name: string;
  campaign_slug: string;
  description: string | null;
  goal: string | null;
  enrollment_type: 'manual' | 'auto_trigger';
  auto_enrollment_trigger: any;
  allow_re_enrollment: boolean;
  send_time_preference: 'immediate' | 'business_hours' | 'optimal';
  business_hours_start: string;
  business_hours_end: string;
  timezone: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  is_active: boolean;
  // Product Journey scoping
  journey_type?: 'classic' | 'product_journey';
  product_id_fk?: number | null;
  anchor_event?: DripAnchorEvent | null;
  // Test mode: every send goes to test_recipient_phone, never the customer. New
  // journeys are created with this ON.
  test_mode?: boolean;
  test_recipient_phone?: string | null;
  // Audience: what this journey applies to. `audience_targets` rides along with the
  // list response so the journeys screen can resolve coverage without an N+1.
  audience_scope?: AudienceScope;
  audience_targets?: AudienceTarget[];
  total_enrolled: number;
  total_completed: number;
  total_unsubscribed: number;
  tags: string[];
  steps?: DripCampaignStep[];
  created_at: string;
  updated_at: string;
}

// The event a product journey is anchored to for enrollment + step timing.
// Trigger key. The API catalogue (GET /drip/drip-campaigns/triggers) is the source of
// truth, so this is a string rather than a union — adding a trigger server-side must
// not require a frontend release to become selectable.
export type DripAnchorEvent = string;

export interface DripCampaignEnrollment {
  id: number;
  uuid: string;
  campaign_id_fk: number;
  lead_id_fk: number;
  lead_uuid: string;
  enrollment_source: 'manual' | 'auto_trigger' | 'import';
  enrolled_at: string;
  current_step_order: number;
  status: 'active' | 'paused' | 'completed' | 'unsubscribed' | 'bounced' | 'failed';
  completed_at: string | null;
  unsubscribed_at: string | null;
  emails_sent: number;
  emails_opened: number;
  emails_clicked: number;
  lead?: any;
}

export interface ClientBranding {
  id: number;
  uuid: string;
  client_id_fk: number;
  logo_url: string | null;
  logo_width: number;
  logo_height: number;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  company_name: string | null;
  company_address: string | null;
  support_email: string | null;
  support_phone: string | null;
  website_url: string | null;
  from_email: string | null;
  from_name: string | null;
  email_verified: boolean;
  email_verified_at: string | null;
  social_links: Record<string, string>;
  footer_text: string | null;
  show_unsubscribe_link: boolean;
  unsubscribe_text: string;
  show_address: boolean;
  show_social_links: boolean;
  custom_disclaimer: string | null;
}

// ===== CAMPAIGNS =====

export const getCampaigns = async (filters?: {
  status?: string;
  enrollment_type?: string;
}): Promise<ApiResponse<DripCampaign[]>> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.enrollment_type) params.append('enrollment_type', filters.enrollment_type);

  const url = params.toString() ? `/drip/drip-campaigns?${params}` : '/drip/drip-campaigns';
  return api.get<DripCampaign[]>(url);
};

export const getCampaignById = async (
  campaignId: number
): Promise<ApiResponse<DripCampaign>> => {
  return api.get<DripCampaign>(`/drip/drip-campaigns/${campaignId}`);
};

export const createCampaign = async (
  data: Partial<DripCampaign>
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: '/drip/drip-campaigns',
    method: api.Method.POST,
    body: data,
  });
};

export const updateCampaign = async (
  campaignId: number,
  data: Partial<DripCampaign>
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: `/drip/drip-campaigns/${campaignId}`,
    method: api.Method.PUT,
    body: data,
  });
};

export const deleteCampaign = async (
  campaignId: number
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/drip-campaigns/${campaignId}`,
    method: api.Method.DELETE,
  });
};

/**
 * Activate a journey.
 *
 * A journey with test mode OFF messages real customers, so the API rejects it with 409
 * unless `confirmLive` is passed. Callers must show a confirmation before setting it.
 */
export const activateCampaign = async (
  campaignId: number,
  confirmLive = false
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: `/drip/drip-campaigns/${campaignId}/activate`,
    method: api.Method.POST,
    data: { confirm_live: confirmLive },
  });
};

export const pauseCampaign = async (
  campaignId: number
): Promise<ApiResponse<DripCampaign>> => {
  return api.req<DripCampaign>({
    url: `/drip/drip-campaigns/${campaignId}/pause`,
    method: api.Method.POST,
  });
};

export const getCampaignStats = async (
  campaignId: number
): Promise<ApiResponse<any>> => {
  return api.get(`/drip/drip-campaigns/${campaignId}/stats`);
};

// ===== CAMPAIGN STEPS =====

export const addCampaignStep = async (
  campaignId: number,
  data: Partial<DripCampaignStep>
): Promise<ApiResponse<DripCampaignStep>> => {
  return api.req<DripCampaignStep>({
    url: `/drip/drip-campaigns/${campaignId}/steps`,
    method: api.Method.POST,
    body: data,
  });
};

export const updateCampaignStep = async (
  campaignId: number,
  stepId: number,
  data: Partial<DripCampaignStep>
): Promise<ApiResponse<DripCampaignStep>> => {
  return api.req<DripCampaignStep>({
    url: `/drip/drip-campaigns/${campaignId}/steps/${stepId}`,
    method: api.Method.PUT,
    body: data,
  });
};

export const deleteCampaignStep = async (
  campaignId: number,
  stepId: number
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/drip-campaigns/${campaignId}/steps/${stepId}`,
    method: api.Method.DELETE,
  });
};

// ===== ENROLLMENTS =====

export const enrollLead = async (
  campaignId: number,
  leadId: number
): Promise<ApiResponse<DripCampaignEnrollment>> => {
  return api.req<DripCampaignEnrollment>({
    url: `/drip/drip-campaigns/${campaignId}/enroll`,
    method: api.Method.POST,
    body: { leadId },
  });
};

export const getCampaignEnrollments = async (
  campaignId: number,
  filters?: { status?: string }
): Promise<ApiResponse<DripCampaignEnrollment[]>> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);

  const url = params.toString()
    ? `/drip/drip-campaigns/${campaignId}/enrollments?${params}`
    : `/drip/drip-campaigns/${campaignId}/enrollments`;

  return api.get<DripCampaignEnrollment[]>(url);
};

export const unenrollLead = async (
  enrollmentId: number,
  reason?: string
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/drip-campaigns/enrollments/${enrollmentId}/unenroll`,
    method: api.Method.POST,
    body: { reason },
  });
};

// ===== TEMPLATES =====

export const getTemplates = async (filters?: {
  category?: string;
}): Promise<ApiResponse<DripEmailTemplate[]>> => {
  const params = new URLSearchParams();
  if (filters?.category) params.append('category', filters.category);

  const url = params.toString() ? `/drip/drip-campaigns/templates?${params}` : '/drip/drip-campaigns/templates';
  return api.get<DripEmailTemplate[]>(url);
};

export const getTemplateById = async (
  templateId: number
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.get<DripEmailTemplate>(`/drip/drip-campaigns/templates/${templateId}`);
};

export const createTemplate = async (
  data: Partial<DripEmailTemplate>
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.req<DripEmailTemplate>({
    url: '/drip/drip-campaigns/templates',
    method: api.Method.POST,
    body: data,
  });
};

export const updateTemplate = async (
  templateId: number,
  data: Partial<DripEmailTemplate>
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.req<DripEmailTemplate>({
    url: `/drip/drip-campaigns/templates/${templateId}`,
    method: api.Method.PUT,
    body: data,
  });
};

export const deleteTemplate = async (
  templateId: number
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/drip-campaigns/templates/${templateId}`,
    method: api.Method.DELETE,
  });
};

export const cloneTemplate = async (
  templateId: number
): Promise<ApiResponse<DripEmailTemplate>> => {
  return api.req<DripEmailTemplate>({
    url: `/drip/drip-campaigns/templates/${templateId}/clone`,
    method: api.Method.POST,
  });
};

export const previewTemplate = async (
  templateId: number,
  sampleData: Record<string, string>
): Promise<ApiResponse<{
  subject: string;
  html: string;
  plainText: string;
  variables: any[];
}>> => {
  return api.req<{
    subject: string;
    html: string;
    plainText: string;
    variables: any[];
  }>({
    url: `/drip/drip-campaigns/templates/${templateId}/preview`,
    method: api.Method.POST,
    body: sampleData,
  });
};

// ===== CLIENT BRANDING =====

export const getClientBranding = async (): Promise<ApiResponse<ClientBranding>> => {
  return api.get<ClientBranding>('/drip/drip-campaigns/branding');
};

export const updateClientBranding = async (
  data: Partial<ClientBranding>
): Promise<ApiResponse<ClientBranding>> => {
  return api.req<ClientBranding>({
    url: '/drip/drip-campaigns/branding',
    method: api.Method.PUT,
    body: data,
  });
};

export const verifyBrandingEmail = async (): Promise<ApiResponse<{
  success: boolean;
  message: string;
  email: string;
}>> => {
  return api.req<{
    success: boolean;
    message: string;
    email: string;
  }>({
    url: '/drip/drip-campaigns/branding/verify-email',
    method: api.Method.POST,
  });
};

export const checkEmailVerificationStatus = async (): Promise<ApiResponse<{
  success: boolean;
  verified: boolean;
  status: string;
  message: string;
  email: string;
  verifiedAt: string | null;
}>> => {
  return api.get<{
    success: boolean;
    verified: boolean;
    status: string;
    message: string;
    email: string;
    verifiedAt: string | null;
  }>('/drip/drip-campaigns/branding/email-status');
};

// ===== STATUS OPTIONS =====

export interface StatusOption {
  opt_id: number;
  opt_value: string;
  opt_name: string;
}

export const getStatusOptions = async (): Promise<ApiResponse<StatusOption[]>> => {
  return api.get<StatusOption[]>('/integrations/crm/status-options');
};

// ===== PRODUCT JOURNEYS =====
// A Product Journey is a drip campaign with journey_type='product_journey'
// scoped to a Shopify product. It reuses the entire drip campaign backend.

// ===== TRIGGERS =====
// The catalogue is served by the API so the builder can never offer a trigger the
// engine does not implement. Do not hardcode a second copy of this list here.

export interface TriggerConfigField {
  name: string;
  type: 'int_days' | 'select';
  label: string;
  default?: string | number;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
}

export interface JourneyTrigger {
  key: string;
  label: string;
  description: string;
  source: string;
  provides: string[];
  config: TriggerConfigField[];
  available: boolean;
}

export const getJourneyTriggers = async (): Promise<ApiResponse<JourneyTrigger[]>> => {
  return api.get<JourneyTrigger[]>('/drip/drip-campaigns/triggers');
};

export interface NodeTypeMeta {
  value: DripNodeType;
  label: string;
  description: string;
  reserved?: boolean;
}

// The node palette for the journey builder. 'ai_call' is reserved (Engati).
export const JOURNEY_NODE_TYPES: NodeTypeMeta[] = [
  { value: 'email', label: 'Email', description: 'Send an email from a template' },
  { value: 'whatsapp', label: 'WhatsApp', description: 'Send an approved WhatsApp template' },
  { value: 'delay', label: 'Wait', description: 'Wait before the next step' },
  { value: 'condition', label: 'Branch', description: 'If/then on customer response' },
  { value: 'agent_call', label: 'Agent call', description: 'Route a call task to an agent via Round Robin' },
  { value: 'auto_dial', label: 'Auto-dial', description: 'System-initiated outbound call' },
  { value: 'ai_call', label: 'AI call', description: 'Automated AI voice call (Engati)', reserved: true },
];

// Product journeys are just campaigns filtered by journey_type.
export const getProductJourneys = async (): Promise<ApiResponse<DripCampaign[]>> => {
  const res = await getCampaigns();
  if (res.success && Array.isArray(res.data)) {
    return {
      ...res,
      data: res.data.filter((c) => c.journey_type === 'product_journey'),
    };
  }
  return res;
};

// Create a draft journey scoped to a product, then the builder edits its steps.
/**
 * Create a journey.
 *
 * `product_id_fk` is optional: it is only meaningful for the 'product' audience scope.
 * A category or all-products journey has no single product, and forcing one was what
 * made the old product-first screen misrepresent them.
 */
export const createProductJourney = async (params: {
  campaign_name: string;
  anchor_event: DripAnchorEvent;
  product_id_fk?: number | null;
}): Promise<ApiResponse<DripCampaign>> => {
  return createCampaign({
    campaign_name: params.campaign_name,
    description: `Automated multi-channel journey — ${params.campaign_name}`,
    journey_type: 'product_journey',
    product_id_fk: params.product_id_fk ?? null,
    anchor_event: params.anchor_event,
    enrollment_type: 'auto_trigger',
    send_time_preference: 'business_hours',
    timezone: 'Asia/Kolkata',
    status: 'draft',
  } as Partial<DripCampaign>);
};

// ===== SCHEDULED CALLS (journey-raised call tasks) =====

export interface ScheduledCall {
  id: number;
  uuid: string;
  sent_at: string | null;
  outcome: string | null;
  call_log_uuid: string | null;
  age_hours: number;
  step_name: string;
  step_order: number;
  campaign_id: number;
  campaign_name: string;
  test_mode: boolean;
  enrollment_id: number;
  delivered_at: string | null;
  reorder_due_date: string | null;
  lead_id: number;
  lead_name: string | null;
  lead_phone: string | null;
  lead_bucket: string | null;
  last_remark: string | null;
  product_title: string | null;
  order_name: string | null;
  assigned_user_id: number | null;
  assigned_via: string | null;
  note: string;
}

/**
 * Call tasks raised by journeys.
 *
 * `scope='all'` is honoured for super admins only — the API forces everyone else to
 * their own tasks regardless of what is asked for.
 */
export const getScheduledCalls = async (params?: {
  scope?: 'mine' | 'all';
  status?: 'open' | 'done' | 'all';
}): Promise<ApiResponse<ScheduledCall[]>> => {
  const qs = new URLSearchParams();
  if (params?.scope) qs.append('scope', params.scope);
  if (params?.status) qs.append('status', params.status);
  const q = qs.toString();
  return api.get<ScheduledCall[]>(
    `/drip/drip-campaigns/scheduled-calls${q ? `?${q}` : ''}`
  );
};

// ===== AUDIENCE (what a journey applies to) =====

export type AudienceScope = 'variants' | 'product' | 'products' | 'category' | 'all';

export interface AudienceTarget {
  id: number;
  product_id_fk: number | null;
  category_id_fk: number | null;
  /** A SKU / pack size. 60, 120 and 180 units are a 30, 60 and 90 day supply. */
  variant_id_fk: number | null;
  product_title: string | null;
  category_name: string | null;
  variant_sku: string | null;
  variant_title: string | null;
  /** The product a chosen variant belongs to — for "Ashwagandha · 180 Capsules". */
  variant_product_title: string | null;
  variant_product_id: number | null;
}

export interface CampaignAudience {
  audience_scope: AudienceScope;
  product_id_fk: number | null;
  targets: AudienceTarget[];
}

export const getCampaignAudience = async (
  campaignId: number
): Promise<ApiResponse<CampaignAudience>> => {
  return api.get<CampaignAudience>(`/drip/drip-campaigns/${campaignId}/audience`);
};

/**
 * Set a journey's audience.
 *
 * `targets` is only read for the 'variants', 'products' and 'category' scopes —
 * 'product' uses the campaign's own product_id_fk and 'all' needs none. The API rejects
 * those three scopes with an empty target list, since an empty audience matches nothing.
 */
export const setCampaignAudience = async (
  campaignId: number,
  audience_scope: AudienceScope,
  targets: Array<{ product_id?: number; category_id?: number; variant_id?: number }> = []
): Promise<ApiResponse<any>> => {
  return api.req({
    url: `/drip/drip-campaigns/${campaignId}/audience`,
    method: api.Method.PUT,
    body: { audience_scope, targets },
  });
};
